import type { UserAppRouter } from "@backend/routers/user_app/user_app.router";
import { createORPCClient, onError } from "@orpc/client";
import { RPCLink } from "@orpc/client/fetch";
import { SimpleCsrfProtectionLinkPlugin } from "@orpc/client/plugins";
import { env } from "@superadmin/configs/env.config";
import { resolveOrpcUrl } from "@voicely/web-shared/api_base_url";
import { isAbortedRequest } from "@voicely/web-shared/orpc_errors";
import { toast } from "react-toastify";

/**
 * The tenant dashboard's oRPC client, minus the tenant.
 *
 * `superAdmin.*` procedures build on `rpcProtectedProcedure` — session only,
 * no `x-team-id` — so none of apps/frontend's request machinery applies here:
 * no switch gate, no active-team header seeding, no one-shot deferred that
 * hangs every request until a team id is set. What remains is the base URL
 * resolution and the cookie, and those are kept identical to the frontend's
 * so the two apps deploy behind the same nginx shape.
 */

const orpcBaseUrl = resolveOrpcUrl(env.VITE_API_URL);

const link = new RPCLink({
	url: orpcBaseUrl,
	fetch: (request, init) =>
		globalThis.fetch(request, {
			...init,
			// The session cookie is the only credential this app has.
			credentials: "include",
		}),
	interceptors: [
		onError((error) => {
			const err = error as { message?: string; status?: number };

			// Navigation-cancelled requests are routine, not errors.
			if (isAbortedRequest(error)) return;

			// A 401 mid-session means the cookie expired under us. The router
			// loader owns the redirect; here we only say why the page emptied.
			if (err.status === 401) {
				toast.error("Your session has expired. Please sign in again.", {
					toastId: "session-expired",
				});
				window.location.href = "/auth/login";
				return;
			}

			console.error("[oRPC Error]", err);
		}),
	],
	plugins: [new SimpleCsrfProtectionLinkPlugin()],
});

export const orpcFetch: UserAppRouter = createORPCClient(link);
